// Stripe form to collect a credit card and assign it to the account
// (possibly creating a new stripe subscription). There are many reasons and locations this
// component can be used. Some are:
//
// A trialing account adding a card for the first time.
// A free account upgrading to a trial.
// A previously cancelled customer reinstates their account by adding a new card.
// An account's trial had expired and is reinstated from the /account/failed page.
// A customer replaces the card on the billing settings page.
// An unpaid customer adds their card on the /account/failed paygate page.

import React, { useState } from "react"
import PropTypes from "prop-types"
import { redirectOrReload } from "utils/redirect-or-reload"
import { CardForm } from "./card-form"
import { useStripe } from "@stripe/react-stripe-js"
import { analytics } from "utils/analytics"

export const StripeForm = ({
  options = { redirectLink: "" },
  successCallback,
  buttonText,
  planId,
  modalOptions,
  config
}) => {
  const stripe = useStripe()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const handleSubmit = async (cardNumberElement, billingDetails) => {
    setBusy(true)
    setError(null)

    const result = await stripe.createPaymentMethod({
      type: "card",
      card: cardNumberElement,
      billing_details: {
        address: {
          line1: billingDetails.address,
          city: billingDetails.city,
          state: billingDetails.state,
          country: billingDetails.country,
          postal_code: billingDetails.postalCode
        }
      }
    })

    if (result.error) {
      setError(result.error.message)
      setBusy(false)
      trackFormSubmission("stripe_create_payment_method_error")
    } else {
      const response = await setupNewCard(result.paymentMethod.id)

      if (response.error) {
        setError(response.error.message)
        setBusy(false)
        trackFormSubmission("setup_card_error")
      } else if (response.pending_setup_intent) {
        trackFormSubmission("setup_card_pending_setup_intent")
        handleSetupAuth(
          response.pending_setup_intent,
          result.paymentMethod.id,
          response.confirm_url
        )
      } else if (paymentRequiresAuth(response.subscription)) {
        // Trial-expired case. Payment is immediate for new non-trial subscription.
        trackFormSubmission("setup_card_payment_requires_auth")
        handlePaymentAuth(
          response.subscription.latest_invoice.payment_intent,
          response.confirm_url
        )
      } else {
        trackFormSubmission("setup_card_success")
        handleSuccess()
      }
    }
  }

  async function handleSetupAuth(setupIntent, paymentMethodId, confirmUrl) {
    const response = await stripe.confirmCardSetup(setupIntent.client_secret, {
      payment_method: paymentMethodId
    })

    if (response.error) {
      setBusy(false)
      setError(response.error.message)
    } else if (response.setupIntent.status !== "succeeded") {
      const error = `Could not complete setup: ${response.setupIntent.status}`
      setBusy(false)
      setError(error)
      track3ds(error)
    } else {
      confirmSubscription(confirmUrl, setupIntent, paymentMethodId)
    }
  }

  async function handlePaymentAuth(paymentIntent, confirmUrl) {
    const response = await stripe.confirmCardPayment(
      paymentIntent.client_secret
    )

    if (response.error) {
      setBusy(false)
      setError(response.error.message)
    } else if (response.paymentIntent.status !== "succeeded") {
      const error = `Could not complete payment: ${response.paymentIntent.status}`
      setBusy(false)
      setError(error)
      track3ds(error)
    } else {
      confirmPayment(confirmUrl, paymentIntent)
    }
  }

  async function setupNewCard(paymentMethod) {
    const response = await fetch(`/billing/subscriptions/begin_card_setup`, {
      credentials: "include",
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
      },
      body: JSON.stringify({
        payment_method: paymentMethod,
        free_upgrade: options.freeUpgrade,
        form_location: options.form_location,
        plan_id: planId,
        promo_code: options.promo_code
      })
    })

    return response.json()
  }

  async function confirmSubscription(confirmUrl, setupIntent, paymentMethodId) {
    const response = await fetch(confirmUrl, {
      credentials: "include",
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
      },
      body: JSON.stringify({
        setup_intent_id: setupIntent.id,
        payment_method: paymentMethodId,
        free_upgrade: options.freeUpgrade,
        form_location: options.form_location,
        plan_id: planId,
        promo_code: options.promo_code
      })
    })

    if (response.error) {
      setBusy(false)
      setError(response.error.message)
    } else {
      handleSuccess()
    }
  }

  async function confirmPayment(confirmUrl, paymentIntent) {
    await fetch(confirmUrl, {
      credentials: "include",
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
      },
      body: JSON.stringify({
        payment_intent_id: paymentIntent.id,
        form_location: options.form_location,
        promo_code: options.promo_code
      })
    })

    handleSuccess()
  }

  function trackFormSubmission(submissionResult) {
    analytics.track(
      "Element",
      "Clicked",
      {
        name: "upgrade_submit_card",
        element_type: "button",
        text: buttonText || "Submit card",
        submission_result: submissionResult,
        form_location: options.form_location
      },
      { transport: "beacon" }
    )
  }

  const trackUpdatedCard = () => {
    analytics.track("updated-account-card", {
      url: window.location.pathname,
      form_location: options.form_location,
      promo_code: options.promo_code,
      account_id: window.app.user.account_id
    })
  }

  function track3ds(message) {
    analytics.track("feature_interaction_billing", {
      url: window.location.pathname,
      form_location: options.form_location,
      promo_code: options.promo_code,
      account_id: window.app.user.account_id,
      interaction_name: "interaction_3ds",
      message: message
    })
  }

  function handleSuccess() {
    trackUpdatedCard()

    if (successCallback) {
      successCallback()
    } else {
      redirectOrReload(options.redirectLink)
    }
  }

  function paymentRequiresAuth(subscription) {
    return (
      subscription &&
      subscription.status === "incomplete" &&
      subscription.latest_invoice?.payment_intent
    )
  }

  return (
    <div>
      <CardForm
        buttonText={buttonText}
        planId={options.planId}
        busy={busy}
        onChange={() => setError(null)}
        onSubmit={handleSubmit}
        modalOptions={modalOptions}
        config={config}
      />

      {error && (
        <div className="row mt-4">
          <div className="col-sm-12">
            <div className="errors">{error}</div>
          </div>
        </div>
      )}
    </div>
  )
}

StripeForm.propTypes = {
  options: PropTypes.object,
  successCallback: PropTypes.func,
  buttonText: PropTypes.string,
  planId: PropTypes.string,
  modalOptions: PropTypes.shape({
    handleClose: PropTypes.func
  }),
  config: PropTypes.shape({
    useTooltipForDisabled: PropTypes.bool
  })
}
